<template>
  <NewTopbarWrapperMobile v-if="isSmallScreenAndDown" :expanded="menuExpanded">
    <template #top>
      <div class="top-content">
        <div class>
          <img v-if="logoUrl" class="logo-mobile" :src="logoUrl" :alt="t('logotype')" />
          <FriedGetAcceptLogoSmall
            v-else
            class="logo-mobile getaccept"
            :aria-label="t('logotype')"
          />
        </div>
        <div class="room-controls">
          <CommentHistoryDropdown
            v-if="showContextualCommenting && currentView === DrView.Presentation"
            :participant="participant"
            :participants="participants"
            :owner="owner"
            @go-to-comment="$emit('go-to-comment', $event)"
          />
          <FriedIconButton
            class="button-icon"
            data-external="dsr-top-bar-updates-button"
            @click="$emit('show-feed')"
          >
            <FriedIcon icon="megaphone" />
          </FriedIconButton>
          <FriedIconButton
            class="button-icon"
            data-external="dsr-top-bar-hamburger-button"
            @click="$emit('open-mobile-navigation')"
          >
            <FriedIcon icon="hamburger-menu" />
          </FriedIconButton>
        </div>
      </div>
    </template>
    <template #center>
      <RoomNavigation
        :active-view="currentView"
        :participant-id="participant.id"
        :conversations="conversations"
        :show-logo="false"
        :action-plan-visible="actionPlanVisible"
        :feature-settings="featureSettings"
        published-view
        @navigate-to="
          menuExpanded = false;
          $emit('set-view', $event);
        "
      />
    </template>
    <template v-if="currentView === DrView.Presentation" #bottom>
      <ContentNavigation
        :current-page-id="currentPageId"
        :previous-page-id="previousPageId"
        :next-page-id="nextPageId"
        :pages="pages"
        :participant="participant"
        :expanded="menuExpanded"
        @select-page="$emit('select-page', $event)"
        @select-section="$emit('select-section', $event)"
        @set-menu-expanded="menuExpanded = $event"
      />
    </template>
  </NewTopbarWrapperMobile>
  <NewTopbarWrapper v-else>
    <template #left>
      <RoomNavigation
        :active-view="currentView"
        :participant-id="participant.id"
        :conversations="conversations"
        :logo-url="logoUrl"
        :action-plan-visible="actionPlanVisible"
        :feature-settings="featureSettings"
        published-view
        @navigate-to="$emit('set-view', $event)"
      />
    </template>
    <template #right>
      <div class="top-bar-right">
        <FriedAvatarGroup
          v-if="participants.length > 0 && !isSmallScreen"
          v-tooltip="{
            content: t('click_to_see_recipient_information'),
            placement: Placement.Bottom,
            offset: 8,
            delay: { show: 300, hide: 100 },
          }"
          :visible-avatars="3"
          :margin="14"
          clickable
          :avatars="getRecipientAvatars(participants)"
          @click="$emit('show-participant-overview')"
        />
        <CommentHistoryDropdown
          v-if="showContextualCommenting && currentView === DrView.Presentation"
          :participant="participant"
          :participants="participants"
          :owner="owner"
          @go-to-comment="$emit('go-to-comment', $event)"
        />
        <FriedIconButton
          v-tooltip="{
            content: t('dsr_updates'),
            placement: Placement.Bottom,
            offset: 10,
            delay: { show: 300, hide: 100 },
          }"
          class="button-icon"
          data-external="dsr-top-bar-updates-button"
          @click="$emit('show-feed')"
        >
          <FriedIcon icon="megaphone" />
        </FriedIconButton>
        <FriedButton
          v-if="isPublicRoom"
          v-tooltip="{
            content: !isAllowedToInvite && t('dr_max_participants_reached_tooltip'),
            placement: Placement.Bottom,
            offset: 10,
            delay: { show: 300, hide: 100 },
          }"
          class="share-button"
          data-external="dsr-top-bar-share-button"
          :disabled="!isAllowedToInvite"
          @click="$emit('show-share-modal')"
        >
          {{ t('dsr_share') }}
        </FriedButton>
      </div>
    </template>
  </NewTopbarWrapper>
</template>

<script lang="ts">
import type { Page, Participant } from '@getaccept/dsr-shared-new';
import {
  DrView,
  NewTopbarWrapper,
  RoomNavigation,
  NewTopbarWrapperMobile,
} from '@getaccept/dsr-shared-new';
import { AvatarHelper } from '@getaccept/lib-shared-new/src/helpers/avatars.helper';
import { FriedGetAcceptLogoSmall, Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { defineComponent, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { BrandingHelper } from '@getaccept/lib-shared-new/src/signing-theme/helpers/branding.helper';
import type { SigningTheme } from '@getaccept/lib-shared-new/src/signing-theme/types/theme';
import { storeToRefs } from 'pinia';
import useBreakpoints from '@getaccept/lib-shared-new/src/composables/breakpoints.composable';
import type { DealroomFeatureSettings } from '@getaccept/dsr-shared-new/src/types/dsr';
import { useChatPublishedStore } from '../chat/store/chat-published.store';
import CommentHistoryDropdown from '../../contextual-commenting/components/CommentHistoryDropdown.vue';
import ContentNavigation from './ContentNavigation.vue';

export default defineComponent({
  name: 'TopBar',
  components: {
    NewTopbarWrapper,
    RoomNavigation,
    FriedGetAcceptLogoSmall,
    NewTopbarWrapperMobile,
    ContentNavigation,
    CommentHistoryDropdown,
  },
  props: {
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    isSmallScreen: {
      type: Boolean,
    },
    currentView: {
      required: true,
      type: String as PropType<DrView>,
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      required: true,
    },
    pages: {
      type: Array as PropType<Page[]>,
      default: () => [],
    },
    previousPageId: {
      type: String,
      default: '',
    },
    nextPageId: {
      type: String,
      default: '',
    },
    currentPageId: {
      type: String,
      default: '',
    },
    participant: {
      type: Object as PropType<Participant>,
      default: null,
    },
    owner: {
      type: Object as PropType<Participant>,
      default: null,
    },
    showContextualCommenting: {
      type: Boolean,
    },
    actionPlanVisible: {
      type: Boolean,
    },
    featureSettings: {
      type: Object as PropType<DealroomFeatureSettings>,
      required: true,
    },
    isAllowedToInvite: {
      type: Boolean,
    },
    isPublicRoom: {
      type: Boolean,
    },
  },
  emits: [
    'go-to-comment',
    'show-share-modal',
    'show-feed',
    'open-mobile-navigation',
    'show-participant-overview',
    'set-view',
    'select-page',
    'select-section',
  ],
  setup(props) {
    const { t } = useI18n();
    const logoUrl = computed(() => BrandingHelper.getLogoUrl(props.theme));
    const { conversations } = storeToRefs(useChatPublishedStore());
    const { isSmallScreenAndDown } = useBreakpoints();
    const menuExpanded = ref(false);

    return {
      conversations,
      getRecipientAvatars: AvatarHelper.getRecipientAvatars,
      Placement,
      logoUrl,
      t,
      isSmallScreenAndDown,
      DrView,
      menuExpanded,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/components/scss/dsr-variables';
@import '@getaccept/dsr-shared-new/src/scss/mobile-top-bar';

.share-button {
  margin-left: var(--spacing-100);
}

.top-bar-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 2.5rem;
}

:deep(.button.button-icon) {
  margin-left: var(--spacing-50);
  border-radius: 50%;
}
</style>
